import React, { useState, useRef, useEffect } from 'react';
import { Search, Filter, CalendarDays, ChevronDown, ChevronUp } from 'lucide-react';

const mockMonthlyData = [
    { id: 1, party: 'Deep and sons Pvt. Ltd.', openingBalance: '100000', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '3240678 Cr.' },
    { id: 2, party: 'DAN BA Company', openingBalance: '234456', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '8240656 Cr.' },
    { id: 3, party: 'BACF Company', openingBalance: '00', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '9800874 Cr.' },
    { id: 4, party: 'Aarogya India pvt. ltd.', openingBalance: '00', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '12336 Dr.' },
    { id: 5, party: 'VFX developer company', openingBalance: '00', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '9000875 Cr.' },
    { id: 6, party: 'DAN BA Company', openingBalance: '00', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '3240678 Cr.' },
    { id: 7, party: 'BACF Company', openingBalance: '34456', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '3240678 Cr.' },
    { id: 8, party: 'Aarogya India pvt. ltd.', openingBalance: '00', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '3240678 Cr.' },
    { id: 9, party: 'Rocket and sons Pvt. Ltd', openingBalance: '80009', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '3240678 Cr.' },
    { id: 10, party: 'Sarasen India pvt. ltd', openingBalance: '234456', apr: '123556 Dr.', may: '87896 Dr.', jun: '123556 Cr.', jul: '539452 Cr.', aug: '908898 Cr.', sep: '20008 Dr.', oct: '123556 Cr.', closingBalance: '9340678 Cr.' },
];

const getRelativeDate = (daysOffset) => {
    const date = new Date(new Date().getTime() + daysOffset * 24 * 60 * 60 * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const mockDailyData = [
    { id: 1, date: getRelativeDate(0), party: 'Deep and sons Pvt. Ltd.', balance: '319452', type: 'Dr.' }, // Today
    { id: 2, date: getRelativeDate(-1), party: 'DAN BA Company', balance: '739460', type: 'Dr.' }, // Yesterday
    { id: 3, date: getRelativeDate(-3), party: 'BACF Company', balance: '661121', type: 'Cr.' }, // This week
    { id: 4, date: getRelativeDate(-8), party: 'Deep and sons Pvt. Ltd.', balance: '1200573', type: 'Dr.' }, // Last week
    { id: 5, date: getRelativeDate(-15), party: 'Aarogya India pvt. ltd.', balance: '1001808', type: 'Dr.' }, // This month
    { id: 6, date: getRelativeDate(-45), party: 'BACF Company', balance: '607241', type: 'Cr.' }, // Last month
    { id: 7, date: getRelativeDate(-2), party: 'DAN BA Company', balance: '1027241', type: 'Dr.' }, // This week
    { id: 8, date: getRelativeDate(-9), party: 'BACF Company', balance: '1639585', type: 'Dr.' }, // Last week
    { id: 9, date: getRelativeDate(-28), party: 'DAN BA Company', balance: '1725585', type: 'Dr.' }, // This month
    { id: 10, date: getRelativeDate(-60), party: 'Deep and sons Pvt. Ltd.', balance: '1849502', type: 'Dr.' }, // Last month
];

const dateRanges = [
    'Today',
    'Yesterday',
    'This week',
    'Last week',
    'Last 10 days',
    'Last 30 days',
    'This month',
    'Last month',
    'This year',
    'Last year',
    'Custom range'
];

function LedgerPartners() {
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedRange, setSelectedRange] = useState('This year');
    const dropdownRef = useRef(null);

    // Handle clicking outside the dropdown to close it
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRef]);

    // If selected is 'This year' or 'Last year', show month-wise columns (APR, MAY, JUN...)
    // If selected is 'This month', 'Today', etc., show date-wise columns (DATE, PARTY, BALANCE)
    const isMonthWise = selectedRange.includes('year');

    const activeData = isMonthWise ? mockMonthlyData : mockDailyData;

    const parseDate = (dateStr) => {
        if (!dateStr) return new Date();
        const [day, month, year] = dateStr.split('/');
        return new Date(year, month - 1, day);
    };

    const filterDataByDateRange = (data, range) => {
        if (range.includes('year') || range.includes('custom')) {
            return data;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return data.filter(row => {
            if (!row.date) return true;
            const rowDate = parseDate(row.date);
            rowDate.setHours(0, 0, 0, 0);

            const diffTime = today.getTime() - rowDate.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            switch (range) {
                case 'Today':
                    return diffDays === 0;
                case 'Yesterday':
                    return diffDays === 1;
                case 'This week':
                    return diffDays >= 0 && diffDays < 7;
                case 'Last week':
                    return diffDays >= 7 && diffDays < 14;
                case 'Last 10 days':
                    return diffDays >= 0 && diffDays < 10;
                case 'Last 30 days':
                    return diffDays >= 0 && diffDays < 30;
                case 'This month':
                    return rowDate.getMonth() === today.getMonth() && rowDate.getFullYear() === today.getFullYear();
                case 'Last month':
                    let lastMonth = today.getMonth() - 1;
                    let yearObj = today.getFullYear();
                    if (lastMonth < 0) {
                        lastMonth = 11;
                        yearObj -= 1;
                    }
                    return rowDate.getMonth() === lastMonth && rowDate.getFullYear() === yearObj;
                default:
                    return true;
            }
        });
    };

    const dateFilteredData = filterDataByDateRange(activeData, selectedRange);

    const filteredData = dateFilteredData.filter(row =>
        row.party.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const calculateTotals = () => {
        let opening = 0;
        let closing = 0;

        filteredData.forEach(row => {
            if (isMonthWise) {
                // Opening Balance
                const opStr = (row.openingBalance || '0').toString();
                const opAmt = parseFloat(opStr.replace(/[^0-9.-]/g, '')) || 0;
                if (opStr.includes('Cr.')) opening -= opAmt;
                else opening += opAmt;

                // Closing Balance
                const clStr = (row.closingBalance || '0').toString();
                const clAmt = parseFloat(clStr.replace(/[^0-9.-]/g, '')) || 0;
                if (clStr.includes('Cr.')) closing -= clAmt;
                else closing += clAmt;
            } else {
                // For Daily table, row has 'balance' and 'type'
                const clStr = ((row.balance || '0') + ' ' + (row.type || 'Dr.')).toString();
                const clAmt = parseFloat(clStr.replace(/[^0-9.-]/g, '')) || 0;
                if (clStr.includes('Cr.')) closing -= clAmt;
                else closing += clAmt;

                // Opening balance is generally not listed per-row in daily data mock, so we leave it 0 or calculate if needed
            }
        });

        const formatBalance = (amount) => {
            if (amount === 0) return '00';
            const absAmt = Math.abs(amount).toLocaleString('en-IN');
            return `${absAmt}`;
        };

        return {
            openingTotal: formatBalance(opening),
            closingTotal: formatBalance(closing)
        };
    };

    const totals = calculateTotals();

    return (
        <div className="w-full flex flex-col space-y-6 pb-10">
            {/* Title */}
            <div>
                <h1 className="text-[22px] font-bold text-[#1F3D36]">Ledger</h1>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                    {/* Search bar */}
                    <div className="relative w-full sm:w-[350px]">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Search className="w-4 h-4 text-[#8CACA0]" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by company name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-2 border border-[#D5E1DC] rounded-full focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm placeholder:text-[#A8C1B7]"
                        />
                    </div>

                    {/* Filter button */}
                    <button className="flex-shrink-0 w-[42px] h-[42px] flex items-center justify-center border border-[#D5E1DC] rounded-full bg-white hover:bg-gray-50 transition-colors">
                        <Filter className="w-5 h-5 text-[#8CACA0]" />
                    </button>

                    {/* Date Range Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="flex items-center gap-2 pl-4 pr-3 py-2 border border-[#D5E1DC] rounded-full bg-white hover:bg-gray-50 transition-colors text-sm text-[#4E6E64] font-medium"
                        >
                            <CalendarDays className="w-4 h-4 text-[#F97316]" />
                            <span className="w-[85px] text-left">{selectedRange}</span>
                            {isDropdownOpen ? <ChevronUp className="w-4 h-4 text-[#8CACA0]" /> : <ChevronDown className="w-4 h-4 text-[#8CACA0]" />}
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-[#D5E1DC] rounded-xl shadow-lg z-10 py-2">
                                {dateRanges.map((range, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setSelectedRange(range);
                                            setIsDropdownOpen(false);
                                        }}
                                        className={`nav-item w-full text-left px-4 py-2 text-[13px] hover:bg-teal-50/50 transition-colors ${selectedRange === range ? 'bg-teal-100/40 font-medium text-teal-800' : 'text-[#4E6E64]'}`}
                                    >
                                        {range}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Opening Balance */}
                <div className="text-[#1F3D36] ml-auto">
                    <span className="text-sm font-bold mr-2">Opening balance:</span>
                    <span className="text-[24px] font-medium leading-none">{totals.openingTotal}</span>
                </div>
            </div>

            {/* Table Section */}
            <div className="bg-white border border-[#F1F5F3] rounded-[16px] overflow-hidden shadow-sm flex flex-col min-h-[500px]">
                {/* Responsive Table Wrapper for Horizontal Scroll */}
                <div className="overflow-x-auto w-full">
                    {!isMonthWise ? (
                        // DATE WISE TABLE
                        <div className="min-w-full">
                            {/* Table Header */}
                            <div className="grid grid-cols-[1fr_2fr_1fr] px-8 py-5 border-b border-[#F1F5F3] bg-white gap-4">
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">DATE</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">PARTY</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider text-right">BALANCE</div>
                            </div>

                            {/* Table Body */}
                            <div className="overflow-y-auto max-h-[60vh] pb-4">
                                {filteredData.map((row, index) => (
                                    <div
                                        key={index}
                                        className={`grid grid-cols-[1fr_2fr_1fr] px-8 py-[18px] hover:bg-teal-50/50 transition-colors items-center gap-4 ${index % 2 === 0 ? 'bg-[#FCFEFD]' : 'bg-white'}`}
                                    >
                                        <div className="text-[13px] font-medium text-[#1F3D36]">{row.date}</div>
                                        <div className="text-[13px] font-medium text-[#1F3D36]">{row.party}</div>
                                        <div className="text-[13px] font-medium text-[#1F3D36] text-right">
                                            {row.balance} {row.type}
                                        </div>
                                    </div>
                                ))}
                                {filteredData.length === 0 && (
                                    <div className="p-8 text-center text-gray-500">
                                        No partners found.
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        // MONTH WISE TABLE
                        <div className="min-w-[1200px]">
                            {/* Table Header */}
                            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] px-8 py-5 border-b border-[#F1F5F3] bg-white gap-4">
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">PARTY NAME</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">OPENING BALANCE</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">APR</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">MAY</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">JUN</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">JUL</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">AUG</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">SEP</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">OCT</div>
                                <div className="text-[11px] font-bold text-[#8CACA0] uppercase tracking-wider">CLOSING BALANCE</div>
                            </div>

                            {/* Table Body */}
                            <div className="overflow-y-auto max-h-[60vh] pb-4">
                                {filteredData.map((row, index) => (
                                    <div
                                        key={index}
                                        className={`grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] px-8 py-[18px] hover:bg-teal-50/20 transition-colors items-center gap-4 ${index % 2 === 0 ? 'bg-white' : 'bg-green-50/10'
                                            }`}
                                    >
                                        <div className="text-[13px] font-medium text-[#1F3D36]">{row.party}</div>
                                        <div className="text-[13px] text-[#4E6E64]">{row.openingBalance}</div>
                                        <div className="text-[13px] text-[#4E6E64] whitespace-nowrap">{row.apr}</div>
                                        <div className="text-[13px] text-[#4E6E64] whitespace-nowrap">{row.may}</div>
                                        <div className="text-[13px] text-[#4E6E64] whitespace-nowrap">{row.jun}</div>
                                        <div className="text-[13px] text-[#4E6E64] whitespace-nowrap">{row.jul}</div>
                                        <div className="text-[13px] text-[#4E6E64] whitespace-nowrap">{row.aug}</div>
                                        <div className="text-[13px] text-[#4E6E64] whitespace-nowrap">{row.sep}</div>
                                        <div className="text-[13px] text-[#4E6E64] whitespace-nowrap">{row.oct}</div>
                                        <div className="text-[13px] font-medium text-[#1F3D36] whitespace-nowrap">{row.closingBalance}</div>
                                    </div>
                                ))}
                                {filteredData.length === 0 && (
                                    <div className="p-8 text-center text-gray-500">
                                        No partners found.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end pr-2 pt-1">
                <div className="text-[#1F3D36] flex items-baseline">
                    <span className="text-[15px] font-bold mr-3">Closing balance:</span>
                    <span className="text-[28px] font-medium">{totals.closingTotal}</span>
                </div>
            </div>
        </div>
    );
}

export default LedgerPartners;
